export type NotificationResponse = {
    id: string;
    title: string;
    body: string;
    readAt: string | null;
    createdAt: string;
    development: { id: string; displayId: string; title: string } | null;
};

export type NotificationProps = {
    id: string;
    title: string;
    body: string;
    readAt: string | null;
    createdAt: string;
    developmentId: string | null;
    developmentDisplayId: string | null;
    developmentTitle: string | null;
};

export class NotificationModel {
    private constructor(private readonly props: NotificationProps) {}

    public static with(props: NotificationProps): NotificationModel {
        return new NotificationModel(props);
    }

    public static withFromResponse(response: NotificationResponse): NotificationModel {
        return NotificationModel.with({
            id: response.id,
            title: response.title,
            body: response.body,
            readAt: response.readAt,
            createdAt: response.createdAt,
            developmentId: response.development?.id ?? null,
            developmentDisplayId: response.development?.displayId ?? null,
            developmentTitle: response.development?.title ?? null
        });
    }

    public static withFromResponseList(list: NotificationResponse[]): NotificationModel[] {
        return list.map(NotificationModel.withFromResponse);
    }

    public get id(): string {
        return this.props.id;
    }

    public get title(): string {
        return this.props.title;
    }

    public get body(): string {
        return this.props.body;
    }

    public get readAt(): string | null {
        return this.props.readAt;
    }

    public get createdAt(): string {
        return this.props.createdAt;
    }

    public get development(): { id: string; displayId: string; title: string } | null {
        return this.props.developmentId ? { id: this.props.developmentId, displayId: this.props.developmentDisplayId!, title: this.props.developmentTitle! } : null;
    }

    public toObject(): NotificationProps {
        return { ...this.props };
    }
}

export type NotificationListResponseRaw = {
    data: NotificationResponse[];
    unreadCount: number;
    total: number;
    page: number;
    totalPages: number;
};

export type NotificationListResponseProps = {
    data: NotificationModel[];
    unreadCount: number;
    total: number;
    page: number;
    totalPages: number;
};

export class NotificationListResponseModel {
    private constructor(private readonly props: NotificationListResponseProps) {}

    public static with(props: NotificationListResponseProps): NotificationListResponseModel {
        return new NotificationListResponseModel(props);
    }

    public static withFromResponse(response: NotificationListResponseRaw): NotificationListResponseModel {
        return NotificationListResponseModel.with({
            data: NotificationModel.withFromResponseList(response.data ?? []),
            unreadCount: response.unreadCount,
            total: response.total,
            page: response.page,
            totalPages: response.totalPages
        });
    }

    public get data(): NotificationModel[] {
        return this.props.data;
    }

    public get unreadCount(): number {
        return this.props.unreadCount;
    }

    public get total(): number {
        return this.props.total;
    }

    public get page(): number {
        return this.props.page;
    }

    public get totalPages(): number {
        return this.props.totalPages;
    }

    public toObject(): NotificationListResponseProps {
        return { ...this.props };
    }
}
